import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, getDevUser, setDevUser } from '../api/client';
import type { Facility, Me, Period, Reference, ScoreBand } from '../api/types';

/* ---------------------------------------------------------------------------
   The period selector is the app's single period control. Changing it
   rescopes the dashboard, the scorecard, both date filters and the employee
   feed at once — because every one of those is the same derivation over a
   different window.
   --------------------------------------------------------------------------- */

interface AppState {
  me: Me;
  reference: Reference;
  periodId: number;
  setPeriodId: (id: number) => void;
  period: Period;
  bands: ScoreBand[];
  /** Departments named in Settings → Scorecard Scope; all of them when unset. */
  scopedDepartments: string[];
  /** Facilities named in Settings → Scorecard Scope; all of them when unset. */
  scopedFacilities: Facility[];
  bandColor: (status: string) => { color: string; bg: string };
  can: (resource: string, permission: string) => boolean;
  /** Where a capability applies: null = every location, [] = not granted. */
  locationsFor: (resource: string, permission: string) => string[] | null;
  /** Dev-mode role switcher. There is no switcher in production. */
  devUser: string | null;
  switchDevUser: (upn: string) => void;
}

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp must be used inside <AppProvider>');
  return v;
}

const BAND_TINTS: Record<string, string> = {
  '#6f8f3a': '#eef3e3',
  '#c8622c': '#fbe9de',
  '#a83232': '#f7e2e0',
};

export function AppProvider({ children }: { children: ReactNode }) {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/me'), retry: false });
  const refQuery = useQuery({
    queryKey: ['reference'],
    queryFn: () => api.get<Reference>('/api/reference'),
    enabled: meQuery.isSuccess,
  });

  const [periodId, setPeriodId] = useState<number | null>(null);
  const [devUser, setDevUserState] = useState<string | null>(getDevUser());

  const value = useMemo<AppState | null>(() => {
    const me = meQuery.data;
    const reference = refQuery.data;
    if (!me || !reference) return null;

    const effectivePeriodId = periodId ?? reference.currentPeriodId;
    const period =
      reference.periods.find((p) => p.periodId === effectivePeriodId) ??
      reference.periods.find((p) => p.isCurrent) ??
      reference.periods[0];

    const bandByLabel = new Map(reference.bands.map((b) => [b.label, b]));

    // Filter dropdowns should only offer what could actually be listed, or you
    // can pick a department and get an empty table with no explanation. Editor
    // dropdowns keep using reference.departments / reference.facilities, because
    // moving somebody INTO scope means choosing a value that is not in it yet.
    const named = (key: string) =>
      (reference.settings?.[key] ?? '').split(',').map((x) => x.trim()).filter(Boolean);

    const namedDepartments = named('ScorecardDepartments');
    const namedLocations = named('ScorecardLocations');

    const scopedDepartments = namedDepartments.length
      ? reference.departments.filter((d) => namedDepartments.some((n) => n.toLowerCase() === d.toLowerCase()))
      : reference.departments;

    const scopedFacilities = namedLocations.length
      ? reference.facilities.filter((f) => namedLocations.some((n) => n.toLowerCase() === f.code.toLowerCase()))
      : reference.facilities;

    return {
      me,
      reference,
      periodId: effectivePeriodId,
      setPeriodId,
      period,
      bands: reference.bands,
      scopedDepartments,
      scopedFacilities,
      bandColor: (status: string) => {
        const color = bandByLabel.get(status)?.color ?? '#9a9890';
        return { color, bg: BAND_TINTS[color] ?? '#f1efe9' };
      },
      can: (resource, permission) => me.capabilities.includes(`${resource}:${permission}`),
      locationsFor: (resource, permission) => {
        const codes = me.scopes[`${resource}:${permission}`];
        return codes === undefined ? [] : codes;
      },
      devUser,
      switchDevUser: (upn: string) => {
        setDevUser(upn);
        setDevUserState(upn);
        // A different person means a different capability set and a different
        // location scope, so start clean rather than reconciling caches, and
        // start where that person starts. Reloading the current page instead
        // left the new person looking at whatever the last one had open.
        window.location.assign('/');
      },
    };
  }, [meQuery.data, refQuery.data, periodId, devUser]);

  if (meQuery.isError) {
    const err = meQuery.error as Error;
    // A 403 is the API answering and saying no: this person has no access
    // yet. "Cannot reach the API" would send them looking for the wrong fix.
    if (err instanceof ApiError && err.status === 403) {
      return <NotSetUp message={err.message} devUser={devUser} />;
    }
    return (
      <Bootstrap title="Cannot reach the API">
        <p>
          The scorecard could not load your account. Check that the API is running on{' '}
          <code>http://localhost:5217</code> and that both databases are reachable.
        </p>
        <p style={{ color: 'var(--muted)', fontSize: 12 }}>{String((meQuery.error as Error).message)}</p>
      </Bootstrap>
    );
  }

  if (!value) return <Bootstrap title="Loading the scorecard…">{null}</Bootstrap>;

  if (value.me.capabilities.length === 0) {
    return (
      <NotSetUp
        message={`You're signed in as ${value.me.upn}, but no role has been assigned to you yet.`}
        devUser={devUser}
      />
    );
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function NotSetUp({ message, devUser }: { message: string; devUser: string | null }) {
  return (
    <Bootstrap title="Your account isn't set up yet">
      <p>{message}</p>
      <p style={{ color: 'var(--muted)', fontSize: 12 }}>
        An administrator adds people and assigns their roles on the Administration screen.
      </p>
      {/* Development only: a remembered "View as" user can go stale. */}
      {devUser && (
        <button className="btn btn--sm" onClick={() => { setDevUser(null); window.location.assign('/'); }}>
          Stop viewing as {devUser}
        </button>
      )}
    </Bootstrap>
  );
}

function Bootstrap({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 40 }}>
      <div className="card" style={{ maxWidth: 520 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--blue)', marginBottom: 10 }}>
          CYNTOX BIOHAZARD SOLUTIONS
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>{title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{children}</div>
      </div>
    </div>
  );
}
